import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Clock, Users, BookOpenText, FileText, ChevronRight, Brain, Zap, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { GlassCard, SectionLabel, SeverityBadge, StatusBadge, GradientButton } from "@/components/primitives";

function fmtAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 36e5);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}

export default function EnterpriseMemory({ incidentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/incidents/${incidentId}/memory`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [incidentId]);

  const applyFix = async () => {
    if (!data?.recommended_resolution) return;
    setApplying(true);
    try {
      await api.patch(`/incidents/${incidentId}`, { status: "mitigated", resolution: data.recommended_resolution.resolution });
      window.dispatchEvent(new CustomEvent("incident:refresh"));
    } finally { setApplying(false); }
  };

  if (loading) {
    return (
      <GlassCard className="p-6">
        <SectionLabel>Enterprise Memory Engine</SectionLabel>
        <div className="mt-3 space-y-2">
          {[1,2,3].map((i) => <div key={i} className="h-14 rounded-xl bg-white/[0.03] border border-white/5 relative overflow-hidden"><div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent" /></div>)}
        </div>
      </GlassCard>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="enterprise-memory">
      {/* Hero recommendation */}
      <GlassCard className="p-6 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-cyan-300" />
              <SectionLabel>Enterprise Memory Engine</SectionLabel>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">USP</span>
            </div>
            <h3 className="mt-2 text-xl font-bold tracking-tight">Recommended resolution from <span className="gradient-text">your team&apos;s history</span></h3>
          </div>
          <div className="flex gap-3 text-center">
            <Stat icon={Clock} label="ETA" value={`${data.estimated_time_to_resolve_min}m`} />
            <Stat icon={Sparkles} label="Confidence" value={`${Math.round(data.confidence * 100)}%`} />
            <Stat icon={TrendingUp} label="Success" value={`${data.success_rate_pct}%`} />
          </div>
        </div>

        {data.recommended_resolution ? (
          <div className="relative mt-5 p-4 rounded-2xl bg-white/[0.03] border border-cyan-500/20" data-testid="memory-recommended-fix">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">Best previous fix · {data.recommended_resolution.match_score}% match</div>
              <span className="text-[10px] font-mono text-slate-400">by <span className="text-slate-200">{data.recommended_resolution.engineer}</span></span>
            </div>
            <p className="mt-2 text-sm text-slate-200 leading-relaxed">{data.recommended_resolution.resolution}</p>
            <div className="mt-3 flex items-center gap-2">
              <GradientButton onClick={applyFix} disabled={applying} data-testid="memory-apply-fix"><Zap className="h-4 w-4" /> {applying ? "Applying…" : "Apply this fix"}</GradientButton>
              <Link to={`/app/incidents/${data.recommended_resolution.from_incident_id}`} className="text-xs text-cyan-300 hover:underline">View original incident →</Link>
            </div>
          </div>
        ) : (
          <div className="mt-5 p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-sm text-slate-400">No high-confidence past resolution available — the AI will draft one from scratch.</div>
        )}
      </GlassCard>

      {/* Similar incidents */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between">
          <SectionLabel>Similar past incidents</SectionLabel>
          <span className="text-[10px] font-mono text-cyan-300">{data.similar_incidents.length} matches</span>
        </div>
        <div className="mt-3 space-y-2">
          {data.similar_incidents.length === 0 && <div className="text-xs text-slate-500">No close matches in history.</div>}
          {data.similar_incidents.map((s) => (
            <Link to={`/app/incidents/${s.id}`} key={s.id} className="block p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-cyan-400/30 transition group">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <SeverityBadge value={s.severity} />
                    <StatusBadge value={s.status} />
                    <span className="text-[10px] font-mono text-slate-500 uppercase">{s.service}</span>
                    <span className="text-[10px] font-mono text-slate-500">{fmtAgo(s.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium mt-1 truncate">{s.title}</div>
                  {s.resolution && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{s.resolution}</div>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">{s.match_score}%</span>
                  <span className="text-[10px] text-slate-500">{s.assignee}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-cyan-300" />
              </div>
            </Link>
          ))}
        </div>
      </GlassCard>

      {/* Two column: KB + Documents */}
      <div className="grid md:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <SectionLabel>Related Knowledge</SectionLabel>
          <div className="mt-3 space-y-2">
            {data.kb_articles.length === 0 && <div className="text-xs text-slate-500">No related articles.</div>}
            {data.kb_articles.map((a) => (
              <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/[0.04]">
                <BookOpenText className="h-3.5 w-3.5 text-cyan-300" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{a.title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{a.category}</div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <SectionLabel>Related Documents</SectionLabel>
          <div className="mt-3 space-y-2">
            {data.documents.length === 0 && <div className="text-xs text-slate-500">No related documents.</div>}
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/[0.04]">
                <FileText className="h-3.5 w-3.5 text-cyan-300" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{d.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">by {d.owner}</div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Engineers */}
      {data.engineers.length > 0 && (
        <GlassCard className="p-6">
          <SectionLabel>Engineers who solved this before</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.engineers.map((e) => (
              <div key={e.name} className="flex items-center gap-2 p-2 pr-3 rounded-full bg-white/[0.04] border border-white/10">
                <div className="h-6 w-6 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 grid place-items-center text-[10px] font-bold">{e.name[0]}</div>
                <span className="text-sm">{e.name}</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{e.incidents_resolved}× resolved</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10 min-w-[80px]">
      <div className="flex items-center justify-center gap-1 text-[9px] font-mono uppercase tracking-widest text-slate-500"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-1 text-lg font-mono font-semibold gradient-text">{value}</div>
    </div>
  );
}
